import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Download, FileText, Save, ArrowLeft, Eye, Pencil, X } from "lucide-react";

import api, { API_URL, BULAN, SPP_TAHUN, getToken } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU, NAV_KEPSEK } from "@/lib/nav";

const NILAI_OPTS = ["", "A", "B", "C", "D"];
const NILAI_LEGEND = [
  { k: "A", v: "Mandiri" },
  { k: "B", v: "Sedikit Bantuan" },
  { k: "C", v: "Bantuan Penuh" },
  { k: "D", v: "Tidak mau melakukan instruksi" },
];

// =========================================================
// LIST — Daftar Lembar Evaluasi
// =========================================================

export function EvaluasiPembelajaranList({ nav = NAV_GURU, canCreate = true, canEdit = false, canDelete = false, basePath = "/guru/evaluasi-pembelajaran" }) {
  const navigate = useNavigate();
  const CUR_YEAR = new Date().getFullYear();
  const DEFAULT_TAHUN = SPP_TAHUN.includes(CUR_YEAR) ? CUR_YEAR : SPP_TAHUN[0];
  const [pending, setPending] = useState({ tahun: "", bulan: "" });
  const [applied, setApplied] = useState({ tahun: "", bulan: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (applied.tahun) params.tahun = applied.tahun;
      if (applied.bulan) params.bulan = applied.bulan;
      const { data } = await api.get("/evaluasi-pembelajaran", { params });
      setRows(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const applyFilter = () => setApplied(pending);
  const resetFilter = () => { setPending({ tahun: "", bulan: "" }); setApplied({ tahun: "", bulan: "" }); };

  const del = async (r) => {
    if (!window.confirm(`Hapus lembar evaluasi ${r.nama_siswa} — ${r.bulan_label} ${r.tahun}?`)) return;
    try {
      await api.delete(`/evaluasi-pembelajaran/${r.id}`);
      toast.success("Lembar evaluasi dihapus");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal hapus"); }
  };

  return (
    <Layout nav={nav}>
      <PageTitle title="Evaluasi Pembelajaran" action={
        canCreate ? (
          <button onClick={() => navigate(`${basePath}/baru`)} className="pp-btn-primary" data-testid="eval-buat-btn">
            <Plus className="w-4 h-4" /> Buat Lembar Evaluasi
          </button>
        ) : null
      } />

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="pp-label">Tahun Ajaran</label>
            <select className="pp-input" value={pending.tahun} onChange={(e) => setPending({ ...pending, tahun: e.target.value })} data-testid="eval-filter-tahun">
              <option value="">— Semua —</option>
              {SPP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="pp-label">Bulan</label>
            <select className="pp-input" value={pending.bulan} onChange={(e) => setPending({ ...pending, bulan: e.target.value })} data-testid="eval-filter-bulan">
              <option value="">— Semua —</option>
              {BULAN.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2 sm:col-span-2">
            <button onClick={applyFilter} className="pp-btn-primary flex-1" data-testid="eval-filter-apply">Terapkan</button>
            <button onClick={resetFilter} className="pp-btn-secondary flex-1" data-testid="eval-filter-reset">Reset</button>
          </div>
        </div>
      </section>

      {loading && <div className="pp-card p-12 text-center text-slate-400 italic">Memuat…</div>}
      {!loading && (
        <section className="pp-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="eval-table">
              <thead><tr className="pp-table-header">
                <th className="text-left px-4 py-3">Nama Siswa</th>
                <th className="text-left px-4 py-3">Kelas</th>
                <th className="text-left px-4 py-3">Tahun</th>
                <th className="text-left px-4 py-3">Bulan</th>
                <th className="text-left px-4 py-3">Ketunaan</th>
                {!canCreate && <th className="text-left px-4 py-3">Guru</th>}
                <th className="text-center px-4 py-3">Lihat Lembar Evaluasi</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={canCreate ? 6 : 7} className="text-center py-10 text-slate-400 italic">Belum ada lembar evaluasi.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} className="pp-row" data-testid={`eval-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium">{r.nama_siswa}</td>
                    <td className="px-4 py-3 text-xs">{r.kelas || "—"}</td>
                    <td className="px-4 py-3">{r.tahun}</td>
                    <td className="px-4 py-3">{r.bulan_label}</td>
                    <td className="px-4 py-3 text-xs">{r.ketunaan || "—"}</td>
                    {!canCreate && <td className="px-4 py-3 text-xs">{r.nama_guru}</td>}
                    <td className="px-4 py-3">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => navigate(`${basePath}/${r.id}`)} className="pp-btn-ghost text-xs" data-testid={`eval-open-${r.id}`}>
                          <Eye className="w-4 h-4" /> Lihat
                        </button>
                        {canEdit && (
                          <button onClick={() => navigate(`${basePath}/${r.id}?edit=1`)} className="pp-btn-ghost text-xs text-amber-600" data-testid={`eval-edit-${r.id}`}>
                            <Pencil className="w-4 h-4" /> Edit
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => del(r)} className="pp-btn-ghost text-xs text-rose-600" data-testid={`eval-del-${r.id}`}>
                            <Trash2 className="w-4 h-4" /> Hapus
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </Layout>
  );
}

// =========================================================
// CREATE — Buat Lembar Evaluasi (Guru)
// Struktur bersarang: Kompetensi (A,B,...) → Materi (1,2,...) → Aktivitas (a,b,...)
// Setiap level bisa ditambah & dihapus.
// =========================================================

const letter = (i) => String.fromCharCode(97 + i); // a, b, c...
const LETTER_UP = (i) => String.fromCharCode(65 + i); // A, B, C...

function newAktivitas() {
  return { key: `ak-${Math.random().toString(36).slice(2, 9)}`, aktivitas: "", keterangan: "" };
}
function newMateri() {
  return { key: `mt-${Math.random().toString(36).slice(2, 9)}`, materi: "", aktivitas: [newAktivitas()] };
}
function newKompBlock() {
  return { key: `kp-${Math.random().toString(36).slice(2, 9)}`, kompValue: "", materis: [newMateri()] };
}

export function EvaluasiPembelajaranCreate({ nav = NAV_GURU, basePath = "/guru/evaluasi-pembelajaran" }) {
  const navigate = useNavigate();
  const CUR_YEAR = new Date().getFullYear();
  const DEFAULT_TAHUN = SPP_TAHUN.includes(CUR_YEAR) ? CUR_YEAR : SPP_TAHUN[0];
  const DEFAULT_BULAN = String(new Date().getMonth() + 1).padStart(2, "0");

  const [siswaList, setSiswaList] = useState([]); // pendampingan list
  const [master, setMaster] = useState([]);
  const [siswaId, setSiswaId] = useState("");
  const [tahun, setTahun] = useState(DEFAULT_TAHUN);
  const [bulan, setBulan] = useState(DEFAULT_BULAN);
  const [blocks, setBlocks] = useState([newKompBlock()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pend, mst] = await Promise.all([
          api.get("/pendampingan"),
          api.get("/evaluasi-pembelajaran/master"),
        ]);
        setSiswaList(pend.data || []);
        setMaster(mst.data || []);
      } catch (e) { toast.error("Gagal memuat data awal"); }
    })();
  }, []);

  const currentSiswa = siswaList.find((s) => s.siswa_id === siswaId);
  const kelas = currentSiswa?.kelas || "";
  const ketunaan = currentSiswa?.ketunaan || "";

  // kompValue = `${tingkatId}::${kompetensiNama}`
  const parseKomp = (v) => {
    const [tid, ...rest] = (v || "").split("::");
    return { tid, kn: rest.join("::") };
  };
  const findKompData = (kompValue) => {
    const { tid, kn } = parseKomp(kompValue);
    const t = master.find((x) => x.id === tid);
    const k = t?.kompetensi.find((c) => c.nama === kn);
    return { tingkat: t, komp: k };
  };
  const findMateriData = (kompValue, materiNama) => findKompData(kompValue).komp?.materi.find((m) => m.nama === materiNama);

  const updateBlock = (bi, patch) => setBlocks((bs) => bs.map((b, i) => i === bi ? { ...b, ...patch } : b));
  const updateMateri = (bi, mi, patch) => setBlocks((bs) => bs.map((b, i) => i === bi ? { ...b, materis: b.materis.map((m, j) => j === mi ? { ...m, ...patch } : m) } : b));
  const updateAktivitas = (bi, mi, ai, patch) => setBlocks((bs) => bs.map((b, i) => i === bi ? {
    ...b,
    materis: b.materis.map((m, j) => j === mi ? { ...m, aktivitas: m.aktivitas.map((a, k) => k === ai ? { ...a, ...patch } : a) } : m),
  } : b));

  const addBlock = () => setBlocks((bs) => [...bs, newKompBlock()]);
  const removeBlock = (bi) => setBlocks((bs) => bs.length > 1 ? bs.filter((_, i) => i !== bi) : bs);
  const addMateri = (bi) => updateBlock(bi, { materis: [...blocks[bi].materis, newMateri()] });
  const removeMateri = (bi, mi) => updateBlock(bi, { materis: blocks[bi].materis.length > 1 ? blocks[bi].materis.filter((_, j) => j !== mi) : blocks[bi].materis });
  const addAktivitas = (bi, mi) => updateMateri(bi, mi, { aktivitas: [...blocks[bi].materis[mi].aktivitas, newAktivitas()] });
  const removeAktivitas = (bi, mi, ai) => {
    const cur = blocks[bi].materis[mi].aktivitas;
    updateMateri(bi, mi, { aktivitas: cur.length > 1 ? cur.filter((_, k) => k !== ai) : cur });
  };

  const submit = async () => {
    if (!siswaId) return toast.error("Pilih siswa terlebih dahulu");
    const flat = [];
    for (const b of blocks) {
      const { tid, kn } = parseKomp(b.kompValue);
      if (!tid || !kn) continue;
      for (const m of b.materis) {
        if (!m.materi) continue;
        for (const a of m.aktivitas) {
          if (!a.aktivitas) continue;
          flat.push({ tingkat: tid, kompetensi: kn, materi: m.materi, aktivitas: a.aktivitas, keterangan: a.keterangan || "" });
        }
      }
    }
    if (flat.length === 0) return toast.error("Lengkapi minimal 1 aktivitas");
    setSaving(true);
    try {
      const payload = { siswa_id: siswaId, tahun: parseInt(tahun, 10), bulan, aktivitas: flat };
      const { data } = await api.post("/evaluasi-pembelajaran", payload);
      toast.success("Lembar evaluasi tersimpan");
      navigate(`${basePath}/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={nav}>
      <PageTitle title="Buat Lembar Evaluasi" action={
        <button onClick={() => navigate(basePath)} className="pp-btn-secondary" data-testid="eval-create-back">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </button>
      } />

      {/* Header info */}
      <section className="pp-card p-5 mb-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="pp-label">Nama Siswa Pendampingan *</label>
            <select className="pp-input" value={siswaId} onChange={(e) => setSiswaId(e.target.value)} data-testid="eval-create-siswa">
              <option value="">— Pilih Siswa —</option>
              {siswaList.map((s) => <option key={s.siswa_id} value={s.siswa_id}>{s.nama_siswa}{s.nis_siswa ? ` — ${s.nis_siswa}` : ""}</option>)}
            </select>
            {siswaList.length === 0 && <p className="text-xs text-slate-400 mt-1 italic">Anda belum punya siswa pendampingan.</p>}
          </div>
          <div>
            <label className="pp-label">Kelas</label>
            <input className="pp-input bg-slate-50" value={kelas || ""} readOnly placeholder="Otomatis dari pendampingan" data-testid="eval-create-kelas" />
          </div>
          <div>
            <label className="pp-label">Keterangan (Ketunaan)</label>
            <input className="pp-input bg-slate-50" value={ketunaan || ""} readOnly placeholder="Otomatis dari data siswa" data-testid="eval-create-ketunaan" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="pp-label">Tahun *</label>
              <select className="pp-input" value={tahun} onChange={(e) => setTahun(e.target.value)} data-testid="eval-create-tahun">
                {SPP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="pp-label">Bulan *</label>
              <select className="pp-input" value={bulan} onChange={(e) => setBulan(e.target.value)} data-testid="eval-create-bulan">
                {BULAN.map((b) => <option key={b.v} value={b.v}>{b.label}</option>)}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Kompetensi / Materi / Aktivitas blocks */}
      <section className="pp-card p-5 mb-6">
        <div className="font-semibold text-slate-800 mb-4">Kompetensi, Materi &amp; Aktivitas</div>
        <div className="space-y-5">
          {blocks.map((b, bi) => {
            const { tingkat, komp } = findKompData(b.kompValue);
            return (
              <div key={b.key} className="border border-indigo-100 rounded-xl p-4 bg-indigo-50/30 space-y-3" data-testid={`eval-komp-block-${bi}`}>
                <div className="flex items-center gap-2">
                  <span className="pp-badge-neutral font-bold" data-testid={`eval-komp-letter-${bi}`}>{LETTER_UP(bi)}</span>
                  <select
                    className="pp-input flex-1"
                    value={b.kompValue}
                    onChange={(e) => updateBlock(bi, { kompValue: e.target.value, materis: [newMateri()] })}
                    data-testid={`eval-create-kompetensi-${bi}`}
                  >
                    <option value="">Pilih kompetensi</option>
                    {master.map((t) => (
                      <optgroup key={t.id} label={t.label}>
                        {t.kompetensi.map((k) => <option key={`${t.id}::${k.nama}`} value={`${t.id}::${k.nama}`}>{k.nama}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <button onClick={() => removeBlock(bi)} className="text-rose-500 hover:text-rose-700 p-1" title="Hapus kompetensi" data-testid={`eval-komp-remove-${bi}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {b.kompValue && (
                  <div className="space-y-3 pl-3 border-l-2 border-indigo-200">
                    {b.materis.map((m, mi) => {
                      const materiData = findMateriData(b.kompValue, m.materi);
                      return (
                        <div key={m.key} className="border border-slate-200 rounded-lg p-3 bg-white space-y-3" data-testid={`eval-materi-block-${bi}-${mi}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-500 w-6">{mi + 1}.</span>
                            <select
                              className="pp-input flex-1"
                              value={m.materi}
                              onChange={(e) => updateMateri(bi, mi, { materi: e.target.value, aktivitas: [newAktivitas()] })}
                              data-testid={`eval-create-materi-${bi}-${mi}`}
                            >
                              <option value="">Pilih materi</option>
                              {(komp?.materi || []).map((md) => <option key={md.nama} value={md.nama}>{md.nama}</option>)}
                            </select>
                            <button onClick={() => removeMateri(bi, mi)} className="text-rose-500 hover:text-rose-700 p-1" title="Hapus materi" data-testid={`eval-materi-remove-${bi}-${mi}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {m.materi && (
                            <div className="space-y-2 pl-3 border-l-2 border-slate-200">
                              {m.aktivitas.map((a, ai) => (
                                <div key={a.key} className="space-y-1" data-testid={`eval-aktivitas-block-${bi}-${mi}-${ai}`}>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-slate-400 w-6">{letter(ai)}.</span>
                                    <select
                                      className="pp-input flex-1"
                                      value={a.aktivitas}
                                      onChange={(e) => {
                                        const ak = (materiData?.aktivitas || []).find((x) => x.nama === e.target.value);
                                        updateAktivitas(bi, mi, ai, { aktivitas: e.target.value, keterangan: ak?.keterangan || "" });
                                      }}
                                      data-testid={`eval-create-aktivitas-${bi}-${mi}-${ai}`}
                                    >
                                      <option value="">Pilih aktivitas</option>
                                      {(materiData?.aktivitas || []).map((x) => <option key={x.nama} value={x.nama}>{x.nama}</option>)}
                                    </select>
                                    <button onClick={() => removeAktivitas(bi, mi, ai)} className="text-rose-400 hover:text-rose-600 p-1" title="Hapus aktivitas" data-testid={`eval-aktivitas-remove-${bi}-${mi}-${ai}`}>
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                  {a.keterangan && (
                                    <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded p-2 ml-8 whitespace-pre-line" data-testid={`eval-create-keterangan-${bi}-${mi}-${ai}`}>
                                      <span className="font-semibold text-slate-600">Keterangan:</span> {a.keterangan.slice(0, 300)}{a.keterangan.length > 300 ? "…" : ""}
                                    </div>
                                  )}
                                </div>
                              ))}
                              <button onClick={() => addAktivitas(bi, mi)} className="ml-8 text-xs text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1" data-testid={`eval-aktivitas-add-${bi}-${mi}`}>
                                <Plus className="w-3.5 h-3.5" /> Tambah Aktivitas
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button onClick={() => addMateri(bi)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1" data-testid={`eval-materi-add-${bi}`}>
                      <Plus className="w-3.5 h-3.5" /> Tambah Materi
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={addBlock} className="mt-4 text-sm text-indigo-700 hover:text-indigo-900 font-semibold inline-flex items-center gap-1" data-testid="eval-komp-add">
          <Plus className="w-4 h-4" /> Tambah Kompetensi
        </button>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
          <button onClick={() => navigate(basePath)} className="pp-btn-secondary">Batal</button>
          <button onClick={submit} disabled={saving} className="pp-btn-primary" data-testid="eval-create-submit">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Buat Lembar Evaluasi"}
          </button>
        </div>
      </section>
    </Layout>
  );
}

// =========================================================
// DETAIL / ISI — Lihat & Isi Nilai Lembar Evaluasi
// =========================================================

export function EvaluasiPembelajaranDetail({ nav = NAV_GURU, role = "guru", basePath = "/guru/evaluasi-pembelajaran" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [entries, setEntries] = useState({}); // {aktIdx: [{tanggal,nilai},...] }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const editMode = new URLSearchParams(window.location.search).get("edit") === "1";

  const isGuru = role === "guru";
  const isKepsek = role === "kepala_sekolah";
  const canEditEntries = isGuru || (isKepsek && editMode);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/evaluasi-pembelajaran/${id}`);
      setData(data);
      // normalize entries — ensure 5 slots each
      const e = {};
      (data.aktivitas || []).forEach((_, i) => {
        const cur = data.entries?.[String(i)] || [];
        const arr = [];
        for (let j = 0; j < 5; j++) arr.push({ tanggal: cur[j]?.tanggal || "", nilai: cur[j]?.nilai || "" });
        e[String(i)] = arr;
      });
      setEntries(e);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal memuat"); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setEntry = (aktIdx, colIdx, patch) => {
    setEntries((prev) => ({
      ...prev,
      [String(aktIdx)]: prev[String(aktIdx)].map((e, i) => i === colIdx ? { ...e, ...patch } : e),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/evaluasi-pembelajaran/${id}`, { entries });
      toast.success("Perubahan tersimpan");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  const downloadHref = useMemo(() => {
    if (!data) return null;
    const t = getToken();
    return `${API_URL}/evaluasi-pembelajaran/${id}/pdf${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
  }, [data, id]);

  if (loading) return <Layout nav={nav}><PageTitle title="Lembar Evaluasi" /><div className="pp-card p-12 text-center text-slate-400 italic">Memuat…</div></Layout>;
  if (!data) return null;

  // Group nested: kompetensi (A,B,...) → materi (1,2,...) → aktivitas (a,b,...)
  const kompGroups = [];
  const kSeen = new Map();
  (data.aktivitas || []).forEach((a, i) => {
    const kKey = `${a.tingkat}||${a.kompetensi}`;
    if (!kSeen.has(kKey)) {
      kSeen.set(kKey, kompGroups.length);
      kompGroups.push({ tingkat: a.tingkat, kompetensi: a.kompetensi, materis: [], mSeen: new Map() });
    }
    const kg = kompGroups[kSeen.get(kKey)];
    if (!kg.mSeen.has(a.materi)) {
      kg.mSeen.set(a.materi, kg.materis.length);
      kg.materis.push({ materi: a.materi, items: [] });
    }
    kg.materis[kg.mSeen.get(a.materi)].items.push({ idx: i, ...a });
  });

  return (
    <Layout nav={nav}>
      <PageTitle title="Lembar Evaluasi Pembelajaran" action={
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => navigate(basePath)} className="pp-btn-secondary" data-testid="eval-detail-back">
            <ArrowLeft className="w-4 h-4" /> Kembali
          </button>
          <a href={downloadHref} target="_blank" rel="noreferrer" className="pp-btn-secondary" data-testid="eval-detail-pdf">
            <Download className="w-4 h-4" /> Cetak/PDF
          </a>
          {canEditEntries && (
            <button onClick={save} disabled={saving} className="pp-btn-primary" data-testid="eval-detail-save">
              <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
            </button>
          )}
        </div>
      } />

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-5 gap-3 text-sm">
          <div><div className="text-xs uppercase text-slate-500">Nama Siswa</div><div className="font-semibold">{data.nama_siswa}</div></div>
          <div><div className="text-xs uppercase text-slate-500">NIS</div><div className="font-mono text-xs">{data.nis_siswa || "—"}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Kelas</div><div className="font-semibold">{data.kelas || "—"}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Kebutuhan</div><div className="font-semibold" data-testid="eval-detail-ketunaan">{data.ketunaan || "—"}</div></div>
          <div><div className="text-xs uppercase text-slate-500">Bulan / Tahun</div><div className="font-semibold">{data.bulan_label} {data.tahun}</div></div>
        </div>
      </section>

      {/* Legend penilaian */}
      <section className="pp-card p-4 mb-6 bg-indigo-50/70 border border-indigo-100">
        <div className="text-xs font-semibold text-indigo-900 mb-2">Keterangan Penilaian</div>
        <div className="grid sm:grid-cols-4 gap-2 text-xs text-slate-700">
          {NILAI_LEGEND.map((l) => <div key={l.k}><span className="font-bold text-indigo-700">{l.k}</span> — {l.v}</div>)}
        </div>
      </section>

      {/* Satu tabel utuh: A. Kompetensi → 1. Materi → a. Aktivitas */}
      <section className="pp-card overflow-hidden mb-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="eval-detail-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-3 py-2 w-10">No</th>
              <th className="text-left px-3 py-2 min-w-[220px]">Materi / Aktivitas</th>
              {[1, 2, 3, 4, 5].map((n) => <th key={n} className="text-center px-2 py-2 w-24">Tanggal {n}</th>)}
              <th className="text-left px-3 py-2 min-w-[220px]">Keterangan</th>
            </tr></thead>
            <tbody>
              {kompGroups.map((g, gi) => (
                <React.Fragment key={gi}>
                  <tr className="bg-indigo-100/70" data-testid={`eval-detail-komp-${gi}`}>
                    <td colSpan={8} className="px-3 py-2.5 font-bold text-slate-800">
                      <span className="text-indigo-700 mr-1">{LETTER_UP(gi)}.</span>{g.kompetensi}
                      <span className="ml-2 text-[10px] font-normal uppercase tracking-wider text-slate-500">
                        {g.tingkat === "basic" ? "Tingkat Dasar (Basic)" : "Tingkat Menengah (Intermediate)"}
                      </span>
                    </td>
                  </tr>
                  {g.materis.map((mg, mi) => (
                    <React.Fragment key={mi}>
                      <tr className="bg-slate-100/80" data-testid={`eval-detail-materi-${gi}-${mi}`}>
                        <td colSpan={8} className="px-3 py-2 font-semibold text-slate-700 text-[13px]">{mi + 1}. {mg.materi}</td>
                      </tr>
                      {mg.items.map((it, li) => (
                        <tr key={it.idx} className="pp-row align-top" data-testid={`eval-detail-aktivitas-${it.idx}`}>
                          <td className="px-3 py-2 text-slate-500 font-medium">{letter(li)}.</td>
                          <td className="px-3 py-2">{it.aktivitas}</td>
                          {(entries[String(it.idx)] || []).map((e, ci) => (
                            <td key={ci} className="px-2 py-2">
                              <div className="flex flex-col gap-1">
                                <input
                                  type="number"
                                  min="1"
                                  max="31"
                                  className="pp-input text-center py-1 text-xs"
                                  placeholder="Tgl"
                                  value={e.tanggal}
                                  onChange={(ev) => setEntry(it.idx, ci, { tanggal: ev.target.value.slice(0, 2) })}
                                  disabled={!canEditEntries}
                                  data-testid={`eval-tgl-${it.idx}-${ci}`}
                                />
                                <select
                                  className="pp-input py-1 text-xs text-center font-bold"
                                  value={e.nilai}
                                  onChange={(ev) => setEntry(it.idx, ci, { nilai: ev.target.value })}
                                  disabled={!canEditEntries}
                                  data-testid={`eval-nilai-${it.idx}-${ci}`}
                                >
                                  {NILAI_OPTS.map((n) => <option key={n} value={n}>{n || "—"}</option>)}
                                </select>
                              </div>
                            </td>
                          ))}
                          <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-pre-line">{it.keterangan || "—"}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}

// =========================================================
// Role-bound wrappers
// =========================================================

export function EvaluasiPembelajaranGuru() {
  return <EvaluasiPembelajaranList nav={NAV_GURU} canCreate canEdit={false} canDelete={false} basePath="/guru/evaluasi-pembelajaran" />;
}
export function EvaluasiPembelajaranCreateGuru() {
  return <EvaluasiPembelajaranCreate nav={NAV_GURU} basePath="/guru/evaluasi-pembelajaran" />;
}
export function EvaluasiPembelajaranDetailGuru() {
  return <EvaluasiPembelajaranDetail nav={NAV_GURU} role="guru" basePath="/guru/evaluasi-pembelajaran" />;
}
export function EvaluasiPembelajaranKepsek() {
  return <EvaluasiPembelajaranList nav={NAV_KEPSEK} canCreate={false} canEdit canDelete basePath="/kepsek/evaluasi-pembelajaran" />;
}
export function EvaluasiPembelajaranDetailKepsek() {
  return <EvaluasiPembelajaranDetail nav={NAV_KEPSEK} role="kepala_sekolah" basePath="/kepsek/evaluasi-pembelajaran" />;
}
